import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save, ShieldCheck, UserPlus } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle } from "@/pages/admin/AdminLayout";

const In = ({ label, id, ...rest }) => (
  <label className="block text-sm">
    <span className="font-semibold text-slate-700">{label}</span>
    <input data-testid={id} {...rest} className="mt-1 w-full h-10 border border-slate-300 rounded-md px-3 bg-white outline-none focus:border-[#172554]" />
  </label>
);
const Card = ({ title, desc, children }) => (
  <div className="bg-white border rounded-lg p-5 space-y-4">
    <div><h2 className="font-display text-lg font-bold text-[#172554]">{title}</h2>{desc && <p className="text-xs text-slate-500">{desc}</p>}</div>
    {children}
  </div>
);

function General({ s, set }) {
  return (
    <Card title="Mağaza & WhatsApp Sipariş" desc="WhatsApp numarası sitedeki yeşil sipariş butonunda ve sipariş sonrası ekranda kullanılır.">
      <div className="grid sm:grid-cols-2 gap-4">
        <In id="set-store-name" label="Mağaza adı" value={s.store_name} onChange={(e) => set({ store_name: e.target.value })} />
        <In id="set-support-phone" label="Sipariş & destek telefonu" value={s.support_phone} onChange={(e) => set({ support_phone: e.target.value })} placeholder="0xxx xxx xx xx" />
        <In id="set-whatsapp" label="WhatsApp sipariş numarası" value={s.whatsapp_number} onChange={(e) => set({ whatsapp_number: e.target.value })} placeholder="05xx xxx xx xx" />
        <In id="set-whatsapp-msg" label="WhatsApp hazır mesaj" value={s.whatsapp_message} onChange={(e) => set({ whatsapp_message: e.target.value })} />
      </div>
      <label className="block text-sm"><span className="font-semibold">Kayan kampanya yazıları (her satır bir yazı)</span>
        <textarea data-testid="set-marquee" rows={3} value={(s.marquee || []).join("\n")} onChange={(e) => set({ marquee: e.target.value.split("\n") })} className="mt-1 w-full border rounded-md p-2" />
      </label>
    </Card>
  );
}

function Ibans({ s, set }) {
  const upd = (i, k, v) => set({ ibans: s.ibans.map((b, j) => (j === i ? { ...b, [k]: v } : b)) });
  return (
    <Card title="IBAN / Banka Hesapları" desc="Havale/EFT seçen müşterilere gösterilir.">
      {s.ibans.map((b, i) => (
        <div key={i} className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
          <In id={`iban-bank-${i}`} label="Banka" value={b.bank} onChange={(e) => upd(i, "bank", e.target.value)} />
          <In id={`iban-holder-${i}`} label="Hesap sahibi" value={b.holder} onChange={(e) => upd(i, "holder", e.target.value)} />
          <In id={`iban-no-${i}`} label="IBAN" value={b.iban} onChange={(e) => upd(i, "iban", e.target.value)} />
          <button data-testid={`iban-remove-${i}`} onClick={() => set({ ibans: s.ibans.filter((_, j) => j !== i) })} className="h-10 w-10 border rounded-md text-red-600 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <button data-testid="iban-add" onClick={() => set({ ibans: [...s.ibans, { bank: "", holder: "", iban: "TR" }] })} className="h-9 px-3 rounded-md border text-sm font-semibold flex items-center gap-1"><Plus className="h-4 w-4" />Hesap ekle</button>
    </Card>
  );
}

function Cargo({ s, set }) {
  const upd = (i, k, v) => set({ cargo_companies: s.cargo_companies.map((c, j) => (j === i ? { ...c, [k]: v } : c)) });
  return (
    <Card title="Kargo Seçenekleri" desc="Aktif firmalar ödeme sayfasında müşteriye sunulur. Ürün bazında 'Kargo bizden' seçimi Ürün Yönetimi'ndedir.">
      <div className="grid sm:grid-cols-2 gap-4">
        <In id="set-free-threshold" label="Ücretsiz kargo alt limiti (TL, 0 = kapalı)" type="number" value={s.free_shipping_threshold} onChange={(e) => set({ free_shipping_threshold: Number(e.target.value) })} />
        <In id="set-cod-fee" label="Kapıda ödeme hizmet bedeli (TL)" type="number" value={s.cod_fee} onChange={(e) => set({ cod_fee: Number(e.target.value) })} />
      </div>
      <div className="divide-y border rounded-md">
        {s.cargo_companies.map((c, i) => (
          <div key={i} className="flex items-center gap-3 p-2">
            <Switch data-testid={`cargo-active-${i}`} checked={c.active} onCheckedChange={(v) => upd(i, "active", v)} />
            <input data-testid={`cargo-name-${i}`} value={c.name} onChange={(e) => upd(i, "name", e.target.value)} className="flex-1 h-9 border rounded px-2 text-sm" />
            <input data-testid={`cargo-price-${i}`} type="number" step="0.01" value={c.price} onChange={(e) => upd(i, "price", Number(e.target.value))} className="w-28 h-9 border rounded px-2 text-sm" />
            <span className="text-xs text-slate-500">TL</span>
            <button onClick={() => set({ cargo_companies: s.cargo_companies.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
      <button data-testid="cargo-add" onClick={() => set({ cargo_companies: [...s.cargo_companies, { name: "", price: 0, active: true }] })} className="h-9 px-3 rounded-md border text-sm font-semibold flex items-center gap-1"><Plus className="h-4 w-4" />Kargo firması ekle</button>
    </Card>
  );
}

function KeyGuide() {
  const steps = {
    PayTR: [
      ["paytr.com/uye-isyeri-olun", "https://www.paytr.com/uye-isyeri-olun", "adresinden üye işyeri başvurusu yapın, onay bekleyin."],
      ["PayTR Mağaza Paneli", "https://www.paytr.com", "→ Destek & Kurulum → Entegrasyon Bilgileri: Mağaza No (sadece rakam), Mağaza Parola, Mağaza Gizli Anahtar."],
      [null, null, "Aynı sayfada Bildirim URL olarak yukarıdaki adresi yazıp kaydedin."],
      [null, null, "Bu üç bilgiyi sohbette 'PayTR anahtarlarımı ekle' yazıp açılan güvenli karta girin. Test ödemesinden sonra PayTR'den Canlı Mod isteyin."],
    ],
    iyzico: [
      ["iyzico.com", "https://www.iyzico.com", "üzerinden üye işyeri başvurusu yapın."],
      ["iyzico Merchant Panel", "https://merchant.iyzipay.com", "→ Ayarlar → Firma Ayarları: API Anahtarı ve Güvenlik Anahtarı (Secret Key)."],
      ["Deneme için sandbox paneli", "https://sandbox-merchant.iyzipay.com", "— buradaki anahtarlar sadece deneme modunda çalışır."],
      [null, null, "İki anahtarı sohbette 'iyzico anahtarlarımı ekle' yazıp açılan güvenli karta girin."],
    ],
  };
  return (
    <details className="border rounded-md p-4 bg-slate-50" data-testid="payment-key-guide">
      <summary className="cursor-pointer font-bold text-sm text-[#172554]">Anahtarları nasıl alacağım? (Adım adım tarif)</summary>
      <div className="grid md:grid-cols-2 gap-4 mt-3">
        {Object.entries(steps).map(([name, list]) => (
          <div key={name}>
            <div className="font-semibold text-sm mb-2">{name}</div>
            <ol className="list-decimal pl-5 space-y-1.5 text-sm text-slate-700">
              {list.map(([label, href, text], i) => (
                <li key={i}>{href && <a href={href} target="_blank" rel="noreferrer" className="text-[#16A34A] font-semibold underline">{label}</a>} {text}</li>
              ))}
            </ol>
          </div>
        ))}
      </div>
    </details>
  );
}

function Payments({ s, set }) {
  const P = [["eft", "Havale / EFT"], ["card", "Kredi Kartı (Online)"], ["cod_cash", "Kapıda Nakit"], ["cod_card", "Kapıda Kredi Kartı"]];
  const { data: ready = {} } = useQuery({ queryKey: ["payment-providers"], queryFn: () => api.get("/admin/payment-providers").then((r) => r.data) });
  const prov = (p, k, v) => set({ [p]: { ...(s[p] || {}), [k]: v } });
  const Status = ({ ok }) => (
    <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${ok ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{ok ? "Anahtarlar tanımlı" : "Anahtar bekleniyor"}</span>
  );
  const base = window.location.origin;
  return (
    <Card title="Ödeme Yöntemleri" desc="Seçtiğiniz sağlayıcının anahtarları tanımlıysa kart ödemeleri gerçek tahsilat yapar; tanımlı değilse kart ödemesi TEST modunda kalır.">
      <div className="grid sm:grid-cols-2 gap-2">
        {P.map(([k, l]) => (
          <label key={k} className="flex items-center justify-between border rounded-md p-3 text-sm font-semibold">{l}
            <Switch data-testid={`pay-toggle-${k}`} checked={!!s.payments[k]} onCheckedChange={(v) => set({ payments: { ...s.payments, [k]: v } })} />
          </label>
        ))}
      </div>
      <label className="block text-sm"><span className="font-semibold">Kart ödeme sağlayıcısı</span>
        <select data-testid="set-card-provider" value={s.card_provider} onChange={(e) => set({ card_provider: e.target.value })} className="mt-1 w-full h-10 border rounded-md px-2">
          <option value="iyzico">iyzico</option><option value="paytr">PayTR</option>
        </select>
      </label>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-3 border rounded-md p-4" data-testid="provider-iyzico">
          <div className="flex items-center justify-between"><div className="font-bold">iyzico</div><Status ok={ready.iyzico} /></div>
          <label className="flex items-center justify-between text-sm">Deneme ortamı (sandbox)
            <Switch data-testid="iyzico-sandbox" checked={s.iyzico?.sandbox !== false} onCheckedChange={(v) => prov("iyzico", "sandbox", v)} />
          </label>
          <p className="text-xs text-slate-500">Canlı tahsilat için sandbox'ı kapatın ve canlı anahtarları tanımlayın.</p>
        </div>
        <div className="space-y-3 border rounded-md p-4" data-testid="provider-paytr">
          <div className="flex items-center justify-between"><div className="font-bold">PayTR</div><Status ok={ready.paytr} /></div>
          <label className="flex items-center justify-between text-sm">Test modu
            <Switch data-testid="paytr-test" checked={s.paytr?.test_mode !== false} onCheckedChange={(v) => prov("paytr", "test_mode", v)} />
          </label>
          <p className="text-xs text-slate-500 break-all">PayTR panelinde Bildirim URL olarak şunu yazın: <b className="font-mono">{base}/api/payments/paytr/callback</b></p>
        </div>
      </div>
      <p className="text-xs text-slate-500">Güvenlik için API anahtarları bu ekranda değil, Emergent'in güvenli anahtar bölümünde saklanır.</p>
      <KeyGuide />
    </Card>
  );
}

const MP = {
  hepsiburada: ["Hepsiburada", [["merchant_id", "Merchant ID"], ["username", "API kullanıcı adı"], ["password", "API şifresi"]]],
  trendyol: ["Trendyol", [["seller_id", "Satıcı ID"], ["api_key", "API Key"], ["api_secret", "API Secret"]]],
  pttavm: ["PTT AVM", [["api_key", "API Key"], ["api_secret", "API Secret"]]],
  sahibinden: ["Sahibinden", [["store_id", "Mağaza ID"], ["api_key", "API Key"]]],
};

function Market({ s, set }) {
  const upd = (m, k, v) => set({ marketplaces: { ...s.marketplaces, [m]: { ...s.marketplaces[m], [k]: v } } });
  return (
    <Card title="Pazaryeri Entegrasyonları" desc="Mağaza bilgilerinizi şimdi kaydedin. Ürün/sipariş aktarımı, pazaryerinden API erişimi onaylandığında bağlanacak (şu an bağlantı yok). GittiGidiyor 2022'de kapandığı için listede yok.">
      <div className="grid md:grid-cols-2 gap-4">
        {Object.entries(MP).map(([k, [label, fields]]) => (
          <div key={k} className="border rounded-md p-4 space-y-2" data-testid={`market-${k}`}>
            <div className="flex items-center justify-between"><div className="font-bold">{label}</div>
              <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-600">Bağlantı bekleniyor</span></div>
            {fields.map(([f, l]) => <In key={f} id={`market-${k}-${f}`} label={l} type={/password|secret/.test(f) ? "password" : "text"} value={s.marketplaces[k]?.[f] || ""} onChange={(e) => upd(k, f, e.target.value)} />)}
          </div>
        ))}
      </div>
    </Card>
  );
}

function Admins() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data = [] } = useQuery({ queryKey: ["admin-admins"], queryFn: () => api.get("/admin/admins").then((r) => r.data) });
  const [f, setF] = useState({ name: "", email: "", phone: "", password: "" });
  const add = async () => {
    try {
      await api.post("/admin/admins", f);
      toast.success("Yönetici eklendi. Geçici şifreyi kendisine iletin; ilk girişte değiştirmesi istenecek.");
      setF({ name: "", email: "", phone: "", password: "" });
      qc.invalidateQueries({ queryKey: ["admin-admins"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (a) => {
    try {
      await api.delete(`/admin/admins/${a.id}`);
      toast.success("Yönetici yetkisi kaldırıldı");
      qc.invalidateQueries({ queryKey: ["admin-admins"] });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Card title="Yöneticiler" desc="Eklediğiniz kişi e-posta veya telefonu ile giriş yapıp siteyi tam yetkili yönetebilir. Mevcut bir üyeyi e-postasıyla eklerseniz yönetici yapılır.">
      <div className="grid sm:grid-cols-4 gap-2 items-end">
        <In id="new-admin-name" label="Ad Soyad" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <In id="new-admin-email" label="E-posta" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
        <In id="new-admin-phone" label="Telefon" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="05xx..." />
        <In id="new-admin-password" label="Geçici şifre (min 6)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} />
      </div>
      <button data-testid="new-admin-submit" onClick={add} className="h-10 px-4 rounded-md bg-[#172554] text-white font-bold flex items-center gap-2"><UserPlus className="h-4 w-4" />Yönetici Ekle</button>
      <div className="divide-y border rounded-md">
        {data.map((a) => (
          <div key={a.id} className="flex items-center gap-3 p-3 text-sm" data-testid={`admin-item-${a.id}`}>
            <ShieldCheck className="h-4 w-4 text-[#16A34A]" />
            <div className="flex-1"><b>{a.name}</b> <span className="text-slate-500">{a.username ? `@${a.username}` : ""} {a.email} {a.phone}</span>
              {a.must_change_password && <span className="ml-2 text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Şifre değiştirmedi</span>}</div>
            {a.id !== user?.id && <button data-testid={`admin-remove-${a.id}`} onClick={() => remove(a)} className="text-red-600 text-xs font-semibold">Yetkiyi kaldır</button>}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function ProfileForm() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({ name: user?.name || "", phone: user?.phone || "" });
  const save = async () => {
    try {
      const { data } = await api.put("/auth/profile", f);
      setUser({ ...user, ...data });
      toast.success("Bilgileriniz kaydedildi. Artık telefonunuzla da giriş yapabilirsiniz.");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      {user?.username && <p className="text-sm">Kullanıcı adınız: <b>{user.username}</b></p>}
      <In id="profile-name" label="Ad Soyad" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
      <In id="profile-phone" label="Cep telefonu" type="tel" placeholder="05xx xxx xx xx" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
      <button data-testid="profile-save" onClick={save} className="h-10 px-4 rounded-md bg-[#172554] text-white font-bold">Bilgilerimi Kaydet</button>
    </div>
  );
}

export function PasswordForm({ onDone }) {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const save = async () => {
    try {
      await api.post("/auth/change-password", f);
      toast.success("Şifreniz değiştirildi");
      setF({ current_password: "", new_password: "" });
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-3">
      <In id="pw-current" label="Mevcut şifre" type="password" value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} />
      <In id="pw-new" label="Yeni şifre (en az 6 karakter)" type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} />
      <button data-testid="pw-save" onClick={save} className="h-10 px-4 rounded-md bg-[#16A34A] text-white font-bold">Şifreyi Değiştir</button>
    </div>
  );
}

export default function AdminSettings() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["admin-settings"], queryFn: () => api.get("/admin/settings").then((r) => r.data) });
  const [s, setS] = useState(null);
  useEffect(() => { if (data) setS(data); }, [data]);
  if (!s) return null;
  const set = (patch) => setS({ ...s, ...patch });
  const save = async () => {
    try {
      const { data: d } = await api.put("/admin/settings", { ...s, marquee: (s.marquee || []).filter((x) => x.trim()) });
      qc.setQueryData(["admin-settings"], d);
      qc.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const tabs = [["genel", "Genel & WhatsApp", General], ["iban", "IBAN", Ibans], ["kargo", "Kargo", Cargo], ["odeme", "Ödeme", Payments], ["pazaryeri", "Pazaryerleri", Market]];
  return (
    <>
      <PageTitle title="Ayarlar" desc="Mağaza, IBAN, WhatsApp, kargo, ödeme, pazaryeri ve yönetici ayarları">
        <button data-testid="settings-save-button" onClick={save} className="h-10 px-5 rounded-md bg-[#16A34A] text-white font-bold flex items-center gap-2"><Save className="h-4 w-4" />Kaydet</button>
      </PageTitle>
      <Tabs defaultValue="genel">
        <div className="overflow-x-auto no-scrollbar">
          <TabsList className="w-max">
            {tabs.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`settings-tab-${k}`}>{l}</TabsTrigger>)}
            <TabsTrigger value="yonetici" data-testid="settings-tab-yonetici">Yöneticiler</TabsTrigger>
            <TabsTrigger value="sifre" data-testid="settings-tab-sifre">Hesabım</TabsTrigger>
          </TabsList>
        </div>
        {tabs.map(([k, , C]) => <TabsContent key={k} value={k} className="mt-4"><C s={s} set={set} /></TabsContent>)}
        <TabsContent value="yonetici" className="mt-4"><Admins /></TabsContent>
        <TabsContent value="sifre" className="mt-4 grid lg:grid-cols-2 gap-4"><Card title="Telefon & Bilgilerim" desc="Telefonunuzu yazın; girişte telefon da kullanabilirsiniz."><ProfileForm /></Card><Card title="Şifremi Değiştir"><PasswordForm /></Card></TabsContent>
      </Tabs>
    </>
  );
}
